import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { POSTS } from "@/data/content";

const accentText = { win: "text-[#0055FF]", mac: "text-[#FF3300]", neutral: "text-[#A1A1AA]" };

const BlogPost = () => {
  const { slug } = useParams();
  const index = POSTS.findIndex((p) => p.slug === slug);
  const post = POSTS[index];

  if (!post) {
    return (
      <main data-testid="post-not-found">
        <Nav />
        <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
          <h1 className="font-serif-d text-5xl mb-6">Dispatch not found.</h1>
          <Link to="/" data-testid="not-found-home-link" className="font-oswald text-sm tracking-[0.25em] uppercase border border-white/20 px-8 py-4 hover:border-[#0055FF] hover:text-[#0055FF] transition-colors duration-300">
            Back to the front page
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  const prev = POSTS[(index - 1 + POSTS.length) % POSTS.length];
  const next = POSTS[(index + 1) % POSTS.length];

  return (
    <main data-testid="blog-post-page">
      <Nav />

      <header className="pt-40 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="flex items-center gap-6 mb-8"
          >
            <span className={`font-oswald text-xs tracking-[0.3em] uppercase ${accentText[post.side]}`}>
              {post.category}
            </span>
            <span className="font-mono-p text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA]">
              {post.date} · {post.readTime}
            </span>
          </motion.div>
          <h1 className="font-serif-d text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight" data-testid="post-title">
            <MaskedLine delay={0.2}>{post.title}</MaskedLine>
          </h1>
        </div>
      </header>

      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1, delay: 0.4 }}
        className="max-w-6xl mx-auto px-6 mb-20"
      >
        <div className="border border-white/10 overflow-hidden">
          <img src={post.image} alt={post.title} className="w-full aspect-[21/9] object-cover" data-testid="post-hero-image" />
        </div>
      </motion.div>

      <article className="max-w-3xl mx-auto px-6 pb-24" data-testid="post-body">
        <p className="font-mono-p text-base md:text-lg leading-relaxed text-[#F5F5F5] border-l-2 border-[#0055FF] pl-6 mb-16">
          {post.excerpt}
        </p>
        {post.body.map((section, i) => (
          <Reveal key={i} className="mb-14">
            <h2 className="font-oswald text-2xl md:text-3xl tracking-tight uppercase mb-6 flex items-baseline gap-4">
              <span className="text-sm text-stroke shrink-0">{String(i + 1).padStart(2, "0")}</span>
              {section.heading}
            </h2>
            <div className="prose-body font-mono-p text-sm md:text-base">
              <p>{section.text}</p>
            </div>
          </Reveal>
        ))}
      </article>

      <nav className="border-t border-white/10" data-testid="post-pagination">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2">
          <Link
            to={`/blog/${prev.slug}`}
            data-testid="post-prev-link"
            className="group p-10 border-b md:border-b-0 md:border-r border-white/10 hover:bg-white/[0.02] transition-colors duration-300"
          >
            <span className="flex items-center gap-2 font-mono-p text-[10px] tracking-[0.25em] uppercase text-[#A1A1AA] mb-4">
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-300" /> Previous dispatch
            </span>
            <span className="font-serif-d text-xl md:text-2xl">{prev.title}</span>
          </Link>
          <Link
            to={`/blog/${next.slug}`}
            data-testid="post-next-link"
            className="group p-10 text-right hover:bg-white/[0.02] transition-colors duration-300"
          >
            <span className="flex items-center justify-end gap-2 font-mono-p text-[10px] tracking-[0.25em] uppercase text-[#A1A1AA] mb-4">
              Next dispatch <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
            </span>
            <span className="font-serif-d text-xl md:text-2xl">{next.title}</span>
          </Link>
        </div>
      </nav>

      <Footer />
    </main>
  );
};

export default BlogPost;
